import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query';
import { RequestHeaders } from 'fym-common/src/utils/fetch-types';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { config } from '../../../config/config';
import { AppInfoStateType } from '../../slices/appInfo/state';
import { APIType } from '../types';
import { appInformation } from '../../../utils/appInformation/appInformation';
import { getTimezone } from '../../../utils/deviceInfo/getTimezone';
import { setUserToken } from '../../slices/appInfo/slice';
import { store } from '../../../store/Store';
import { dispatchM24LimitPrompt, parseM24LimitError } from '../../../modules/music24/utils/parseM24LimitError';
import { refreshSessionToken } from '../../../modules/app/session/refreshSessionToken';
import { removeSignedInHint } from '../../../modules/app/session/signedInHintStore';

/**
 * The endpoints that show their own state for a plan-limit refusal (402, 403): the global upgrade
 * prompt does not open on top of it. The AI pictures of the cover editor show the month count and
 * the lock card in the Picture panel (design cover-editor c6, e5). The music video editor shows the
 * Pro dialog and the credit packs for "Make the video" (music-video e4, e5), and the lock card of
 * the AI clips (music-video h4). The dry run of an AI reels plan runs with no click, and the setup
 * shows its refusal in its foot line (rollout-plan f2).
 */
const ENDPOINTS_WITH_OWN_LIMIT_UI: ReadonlySet<string> = new Set([
  'createCoverAiRun',
  'makeMusicVideo',
  'createAiFootage',
  'previewReelsPlan',
]);

/**
 * The endpoints that show their own state for one quota kind only (design limit-upsell a10): the
 * Composer shows the limit upsell card for a 402 of the monthly scheduled posts. Every other
 * refusal of these endpoints (a channel above the plan, the active releases) opens the prompt.
 */
const ENDPOINT_QUOTA_KINDS_WITH_OWN_UI: Readonly<Record<string, ReadonlyArray<string>>> = {
  createPromoPosts: ['scheduledPost'],
};

/** True when the screen of the endpoint shows the limit refusal itself (no global prompt). Pure. */
export function hasOwnLimitUI(endpoint: string, error: unknown): boolean {
  if (ENDPOINTS_WITH_OWN_LIMIT_UI.has(endpoint)) {
    return true;
  }
  const kinds = ENDPOINT_QUOTA_KINDS_WITH_OWN_UI[endpoint];
  if (kinds === undefined) {
    return false;
  }
  const limit = parseM24LimitError(error);
  return limit !== null && limit.code === 'M24_LIMIT_QUOTA' && limit.kind !== null && kinds.includes(limit.kind);
}

export function prepareHeaders(getState: BaseQueryApi['getState'], apiType: APIType) {
  const { appInfo } = getState() as { appInfo: AppInfoStateType };
  const { userToken } = appInfo;
  const headers: RequestHeaders = {
    Accept: 'application/json; charset=utf-8',
    'x-appinfo-target': config.target,
    'x-appinfo-platform': config.platform,
    'x-appinfo-environment': config.environment,
    'x-appinfo-tz': getTimezone(),
  };
  if (apiType === APIType.fym && userToken) {
    headers.Authorization = `Bearer ${userToken}`;
  }
  if (apiType === APIType.music) {
    headers['Content-Type'] = 'application/json';
    headers.Authorization = `Token ${config.musicApiToken}`;
  }
  const { version, platformVersion, buildVersion } = appInformation;
  if (version) {
    headers['x-appinfo-version'] = version;
  }
  if (platformVersion) {
    headers['x-appinfo-platformVersion'] = platformVersion;
  }
  if (buildVersion) {
    headers['x-appinfo-buildVersion'] = buildVersion;
  }

  return headers;
}

async function handleErrorResponse(response: any) {
  const text = await response.text();
  let data: Record<string, any> | undefined;
  let errorMessage: string;
  try {
    data = JSON.parse(text);
    errorMessage = data?.message;
  } catch {
    errorMessage = text;
  }
  throw Object.assign(
    new FYMAPIError({
      message: errorMessage,
      statusCode: response.status,
      internalCode: data?.internalCode,
    }),
    { m24Limit: data?.cause, details: data?.details }
  );
}

export const customBaseQuery =
  (
    baseUrl: string,
    apiType: APIType
  ): BaseQueryFn<
    {
      url: string;
      method: string;
      headers?: Record<string, string>;
      body?: any;
    },
    unknown,
    Error | FYMAPIError
  > =>
  async ({ url, method, body, headers }, api) => {
    try {
      let response = await window.fetch(baseUrl + url, {
        method,
        headers: { ...prepareHeaders(api.getState, apiType), ...headers },
        body,
      });
      if (response.status === 401 && apiType === APIType.fym) {
        // The session token may only be expired. Refresh it once and retry;
        // `refreshSessionToken` signs the user out when the session is over. An admin session
        // ("Log in as") cannot be refreshed: the 401 ends it, with its message.
        const { userToken } = (api.getState() as { appInfo: AppInfoStateType }).appInfo;
        const outcome = userToken ? await refreshSessionToken(userToken, api.dispatch, { unauthorized: true }) : null;
        if (outcome?.kind === 'refreshed') {
          response = await window.fetch(baseUrl + url, {
            method,
            headers: { ...prepareHeaders(api.getState, apiType), ...headers },
            body,
          });
        } else if (outcome === null) {
          api.dispatch(setUserToken({ token: null }));
          store.remove('userToken').catch(console.error);
          removeSignedInHint();
        }
      }
      if (!response.ok) {
        await handleErrorResponse(response);
      }
      // A 204 has no body, and `json()` throws on an empty body.
      if (response.status === 204) {
        return { data: undefined };
      }
      return { data: await response.json() };
    } catch (err: any) {
      const error: Error | FYMAPIError = err;
      console.error('Networking error', error);
      if (!hasOwnLimitUI(api.endpoint, error)) {
        dispatchM24LimitPrompt(api.dispatch, error);
      }
      return { error };
    }
  };
